import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Item, ItemContent, ItemDescription, ItemTitle } from '@xiltrion/ui/item';
import { api, apiGet, ApiError, errorMessage } from '@/api/client';
import type { PublicApp } from '@/api/types';
import { lastSuperadmin, useSession } from '@/app/session';
import { interactionFallback, interactionPath } from './interaction-fallback';
import { EsperaAcceso, MarcoSuite, TituloMarco } from './MarcoSuite';

interface State {
  uid: string;
  prompt: string;
  app: { code: string; name: string; icon: string; webUrl: string };
  tenant: string | null;
  loggedIn: boolean;
  user: { displayName: string; email: string } | null;
  entities: Array<{ slug: string; name: string; profile: string; role: string }>;
}

const OIDC = '/oidc/interaction';

/**
 * Interacción OIDC (spec §4): una app pidió ingreso. Sin sesión → al ingreso y de vuelta; con sesión → elegir
 * entidad (si hay varias) y terminar. La cookie `_interaction` solo viaja a /oidc/interaction/<uid>, por eso las
 * llamadas van con `base`. Si la interacción ya no sirve (venció o ya se usó), nunca muestra el error: vuelve a la app
 * o al ingreso (`interaction-fallback.ts`), con la app y la entidad que trae su URL.
 */
export function Interaction() {
  const { uid = '' } = useParams();
  const [params] = useSearchParams();
  const session = useSession();
  // Sin caché (`gcTime: 0`): sin sesión se va al ingreso y se vuelve aquí ya con ella; con el estado de antes («sin
  // sesión» o «reingreso») volvería al ingreso otra vez y pediría credenciales una y otra vez hasta recargar.
  const { data, error } = useQuery({ queryKey: ['interaction', uid], queryFn: () => api<State>(`/${uid}/state`, { base: OIDC }), retry: false, gcTime: 0 });
  const expired = error instanceof ApiError && error.code === 'INTERACTION_INVALID';
  const appCode = params.get('app');
  const apps = useQuery({ queryKey: ['public-apps'], queryFn: () => apiGet<PublicApp[]>('/public/apps'), enabled: expired && !!appCode, staleTime: 5 * 60_000, retry: false });
  useEffect(() => {
    if (!expired || session.loading || (appCode && apps.isPending)) return;
    const superadmin = session.me ? session.superadmin : lastSuperadmin.get();
    window.location.replace(interactionFallback({ hasSession: !!session.me, superadmin, app: appCode, entidad: params.get('entidad'), webUrl: apps.data?.find((a) => a.code === appCode)?.webUrl ?? null }));
  }, [expired, session.loading, session.me, session.superadmin, appCode, apps.isPending, apps.data, params]);
  const [busy, setBusy] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const finish = async (entitySlug: string) => {
    setBusy(entitySlug);
    setFail(null);
    try {
      const r = await api<{ redirectTo: string }>(`/${uid}/login`, { base: OIDC, method: 'POST', json: { entitySlug } });
      window.location.href = r.redirectTo;
    } catch (e) {
      setFail(errorMessage(e));
      setBusy(null);
    }
  };
  const abort = async () => {
    const r = await api<{ redirectTo: string }>(`/${uid}/abort`, { base: OIDC, method: 'POST', json: {} }).catch(() => null);
    window.location.href = r?.redirectTo ?? '/';
  };
  const single = data?.loggedIn && data.entities.length === 1 ? data.entities[0] : null;
  const wanted = data?.tenant ? data.entities.find((e) => e.slug === data.tenant) : null;
  useEffect(() => {
    if (wanted) void finish(wanted.slug);
    else if (single) void finish(single.slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted?.slug, single?.slug]);
  // Mientras se decide (o la interacción termina sola) no se muestra ninguna pantalla de acceso: solo el indicador. Las
  // únicas pantallas de ingreso son la portada de la entidad y la de Datalog; elegir entidad va en el marco de esta.
  if (expired) return <EsperaAcceso />;
  if (error) return <MarcoSuite><TituloMarco titulo="No se pudo continuar" /><Alert variant="destructive"><AlertDescription>{errorMessage(error)}</AlertDescription></Alert><Button variant="link" className="mt-2 w-full" onClick={() => { window.location.href = '/ingreso'; }}>Volver al ingreso</Button></MarcoSuite>;
  if (!data) return <EsperaAcceso />;
  // Sin sesión: a la portada, con la cortina de esta app abierta, y de vuelta aquí. La
  // de la entidad o, si en este navegador ingresaba un superadministrador, la de Datalog (la negra, sin entidad).
  if (!data.loggedIn) {
    const superadmin = lastSuperadmin.get();
    const entidad = data.tenant && !superadmin ? `&entidad=${encodeURIComponent(data.tenant)}` : '';
    return <Navigate to={`/ingreso?volver=${encodeURIComponent(interactionPath(uid, data.app.code, data.tenant, superadmin))}&app=${encodeURIComponent(data.app.code)}${entidad}`} replace />;
  }
  if ((wanted || single) && !fail) return <EsperaAcceso />;
  return (
    <MarcoSuite superadmin={session.superadmin}>
      <TituloMarco app={data.app} titulo={`Entrar a ${data.app.name}`} subtitulo={data.entities.length ? `Elige la entidad con la que quieres entrar, ${data.user?.displayName}.` : undefined} />
      {fail && <Alert variant="destructive"><AlertDescription>{fail}</AlertDescription></Alert>}
      {data.tenant && !wanted && <Alert variant="warning" className="mb-3"><Icono nombre="mdi:alert-outline" /><AlertDescription>La app pidió la entidad «{data.tenant}», pero no tienes acceso a ella en {data.app.name}.</AlertDescription></Alert>}
      {!data.entities.length ? (
        <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>Tu cuenta ({data.user?.email}) no tiene acceso a {data.app.name} en ninguna entidad. Pide a tu administrador que te lo habilite.</AlertDescription></Alert>
      ) : (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {data.entities.map((e) => (
            <Item key={e.slug} variant="outline" asChild>
              <button type="button" className="w-full text-left" disabled={!!busy} onClick={() => void finish(e.slug)}>
                <Icono nombre="mdi:domain" className="size-5 text-muted-foreground" />
                <ItemContent><ItemTitle>{e.name}</ItemTitle><ItemDescription>{e.role === 'ADMIN' ? 'Administrador' : 'Miembro'} · {e.slug}</ItemDescription></ItemContent>
                {busy === e.slug ? <Icono nombre="mdi:loading" className="animate-spin" /> : <Icono nombre="mdi:chevron-right" />}
              </button>
            </Item>
          ))}
        </div>
      )}
      <Button variant="link" className="mt-3 w-full" onClick={() => void abort()}>Cancelar</Button>
    </MarcoSuite>
  );
}
